import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loading } from "@/components/Loading";

export const AdminCustomers = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/customers").then(({ data }) => setRows(data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="overflow-x-auto paper-card" data-testid="admin-customers">
      <table className="w-full text-left text-base">
        <thead className="bg-[#F2EBE1]"><tr>{["Name", "Email", "Phone", "Sign-in", "Role", "Paid orders", "Joined"].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((u) => (
            <tr key={u.user_id} className="border-t border-[#E6DEC6]" data-testid={`customer-row-${u.user_id}`}>
              <td className="px-3 py-3">{u.name}</td><td className="px-3 py-3">{u.email}</td><td className="px-3 py-3">{u.phone || "—"}</td>
              <td className="px-3 py-3">{u.auth_provider}</td><td className="px-3 py-3">{u.role}</td><td className="px-3 py-3">{u.paid_orders}</td>
              <td className="px-3 py-3 text-sm">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export const AdminRemovals = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/removals").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    if (action === "approve" && !window.confirm("Remove this video? Printed stickers will stop playing it.")) return;
    try { await api.post(`/admin/removals/${id}/${action}`); toast.success("Updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Loading />;
  return (
    <div className="overflow-x-auto paper-card" data-testid="admin-removals">
      <table className="w-full text-left text-base">
        <thead className="bg-[#F2EBE1]"><tr>{["Created", "Order", "From", "Reason", "Status", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.request_id} className="border-t border-[#E6DEC6]" data-testid={`removal-row-${r.request_id}`}>
              <td className="px-3 py-3 text-sm">{new Date(r.created_at).toLocaleString()}</td>
              <td className="px-3 py-3 text-sm">{r.order_id}</td>
              <td className="px-3 py-3 text-sm">{r.requester.type}: {r.requester.email}</td>
              <td className="max-w-sm px-3 py-3">{r.reason}</td>
              <td className="px-3 py-3 font-semibold">{r.status}</td>
              <td className="space-x-2 px-3 py-3">{r.status === "open" && <>
                <button className="btn-outline !min-h-0 !py-1" onClick={() => act(r.request_id, "approve")} data-testid={`removal-approve-${r.request_id}`}>Remove video</button>
                <button className="btn-secondary !min-h-0 !py-1" onClick={() => act(r.request_id, "reject")} data-testid={`removal-reject-${r.request_id}`}>Reject</button>
              </>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {!rows.length && <p className="p-6 text-[#5C4A3E]">No removal requests.</p>}
    </div>
  );
};

const ProductRow = ({ p, reload }) => {
  const [f, setF] = useState({ name: p.name, variant: p.variant, description: p.description, price: p.price });
  const save = async (extra = {}) => {
    try { await api.patch(`/admin/products/${p.product_id}`, { ...f, price: Number(f.price), ...extra }); toast.success("Product saved"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="paper-card grid gap-3 p-5 md:grid-cols-12 md:items-end" data-testid={`admin-product-${p.product_id}`}>
      <div className="md:col-span-3"><label className="label">Name</label><input className="field !min-h-0 !py-2 !text-base" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid={`product-name-input-${p.product_id}`} /></div>
      <div className="md:col-span-4"><label className="label">Size / variant</label><input className="field !min-h-0 !py-2 !text-base" value={f.variant} onChange={(e) => setF({ ...f, variant: e.target.value })} data-testid={`product-variant-input-${p.product_id}`} /></div>
      <div className="md:col-span-2"><label className="label">Price (USD, shipping incl.)</label><input type="number" step="0.01" min="0.5" className="field !min-h-0 !py-2 !text-base" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} data-testid={`product-price-input-${p.product_id}`} /></div>
      <div className="flex gap-2 md:col-span-3">
        <button className="btn-primary !min-h-0 !py-2 !text-base" onClick={() => save()} data-testid={`product-save-${p.product_id}`}>Save</button>
        <button className="btn-outline !min-h-0 !py-2" onClick={() => save({ active: !p.active })} data-testid={`product-toggle-${p.product_id}`}>{p.active ? "Hide" : "Show"}</button>
      </div>
      <div className="md:col-span-12"><label className="label">Description</label><input className="field !min-h-0 !py-2 !text-base" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid={`product-description-input-${p.product_id}`} /></div>
      <p className="text-sm text-[#5C4A3E] md:col-span-12">Status: <b>{p.active ? "Visible to customers" : "Hidden"}</b> · Print type: {p.kind} · Price changes apply to new checkouts only.</p>
    </div>
  );
};

export const AdminProducts = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/products").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  return <div className="space-y-5" data-testid="admin-products">{rows.map((p) => <ProductRow key={p.product_id + p.updated_at} p={p} reload={load} />)}</div>;
};
